import Image from 'next/image'
import Link from 'next/link'
import React, { useState } from 'react'
import { ContentTimestamp } from 'src/components/ContentTimestamp/ContentTimestamp'
import { ContentWriter } from 'src/components/ContentWriter/ContentWriter'
import { HtmlContentViewer } from 'src/components/global/HtmlContentViewer/HtmlContentViewer'
import { PremiumContentIcon } from 'src/components/global/PremiumContentIcon/PremiumContentIcon'
import { SignUpModal } from 'src/components/global/SignUpModal'
import { IEnum_Content_Contenttype } from 'src/graphql/generated/hooks'
import { useDefaultLogo } from 'src/hooks/useDefaultLogo'
import { ContentDataFragment, isContentItem, isContentListItemFragment } from 'src/utils/ContentDataFragment'
import { getContentDefaultImageUrl } from 'src/utils/getContentDefaultImageUrl'
export interface ContentListItemProps {
  item: ContentDataFragment
  showDocumentType?: boolean
  vendor?: boolean
}

export const ContentListItem: React.FC<ContentListItemProps> = ({ item, showDocumentType = false, vendor = false }) => {
  const [showSignUp, setShowSignUp] = useState(false)

  const handleCloseSignUp = () => {
    setShowSignUp(false)
  }

  const contentType = isContentListItemFragment(item)
    ? item.attributes?.contentType
    : isContentItem(item)
      ? item.contentType
      : IEnum_Content_Contenttype.VendorResource
  const documentType = isContentListItemFragment(item) ? item.attributes?.documentType : item.documentType
  const title = (isContentListItemFragment(item) ? item.attributes?.title : item.title) || ''
  const defaultImageUrl = getContentDefaultImageUrl(item)
  const shouldShowDefaultImage =
    contentType &&
    [
      IEnum_Content_Contenttype.Blog,
      IEnum_Content_Contenttype.IndustryAnalysis,
      IEnum_Content_Contenttype.VendorResource,
      IEnum_Content_Contenttype.KnowHow,
      IEnum_Content_Contenttype.LegaltechResourcesForLawyers,
      IEnum_Content_Contenttype.BuyersGuide,
    ].includes(contentType)
  const snippet =
    (isContentListItemFragment(item) ? item.attributes?.snippet : isContentItem(item) ? item.snippet : item.summary) ||
    ''
  const writer = isContentListItemFragment(item)
    ? item.attributes?.writer
    : isContentItem(item)
      ? item.writer
      : item.author
  const url = isContentListItemFragment(item)
    ? item.attributes?.slug
      ? item.attributes.contentType === IEnum_Content_Contenttype.IndustryAnalysis
        ? `/industry-analysis/${item.attributes?.slug}`
        : `/contents/${item.attributes?.slug}`
      : null
    : isContentItem(item)
      ? `/contents/${item.slug}`
      : item.link
  const isInternalLink = isContentListItemFragment(item) || isContentItem(item)
  const logo = useDefaultLogo(defaultImageUrl)

  const logoContent = (
    <>
      {shouldShowDefaultImage && defaultImageUrl ? (
        <Image
          className="bg-white rounded-[.5rem] drop-shadow-md flex items-center justify-center w-[80px] h-[80px] min-w-[80px] min-h-[80px]"
          src={defaultImageUrl}
          alt={title}
          width={80}
          height={80}
          priority
        />
      ) : contentType ? (
        <PremiumContentIcon contentType={contentType} size="lg" defaultImage={defaultImageUrl} />
      ) : (
        <Image
          className="bg-white rounded-[.5rem] drop-shadow-md flex items-center justify-center w-[80px] h-[80px] min-w-[80px] min-h-[80px]"
          src={logo}
          alt={title}
          width={80}
          height={80}
          priority
        />
      )}
    </>
  )

  return (
    <div className="flex flex-row items-center pt-[14px] pb-[14px]">
      <div className="flex flex-row w-full md:items-center">
        <div className="mr-[30px]">
          {url && (
            <Link prefetch={false} className="text-left" href={url} target={isInternalLink ? undefined : '_blank'}>
              {logoContent}
            </Link>
          )}
          {!url && <div className="text-left">{logoContent}</div>}
        </div>

        <div className="grow">
          <div className="w-full flex md:flex-row flex-col justify-between">
            <div className="w-full flex flex-col justify-between">
              <div className="w-full flex justify-between items-center">
                <div className="cursor-pointer text-[13px] text-textdark font-secondary font-medium leading-[20px]">
                  {showDocumentType ? (documentType || '').replace(/_/g, ' ') : (contentType || '').replace(/_/g, ' ')}
                </div>
                <div className="flex">
                  {!vendor && writer && <ContentWriter writer={writer} />}
                  <div className="flex items-center">
                    <div className="mr-3">
                      <ContentTimestamp content={item} />
                    </div>
                  </div>
                  {vendor && (
                    <div className="bg-fog px-[.4rem] py-[.2rem] text-textdark font-bold rounded-[6px] text-[.8rem] tracking-widest leading-5 h-[25px]">
                      Vendor Content
                    </div>
                  )}
                </div>
              </div>
              {url ? (
                <Link
                  prefetch={false}
                  className="text-left text-line-2 cursor-pointer text-[18px] text-textdark font-secondary font-bold mb-1.5"
                  href={url}
                  target={isInternalLink ? undefined : '_blank'}
                >
                  {title}
                </Link>
              ) : (
                <div className="text-left text-line-2 cursor-pointer text-[18px] text-textdark font-secondary font-bold mb-1.5">
                  {title}
                </div>
              )}
            </div>
          </div>
          <HtmlContentViewer
            className="md:pr-[2rem] line-clamp-2 !text-[14px] leading-[18px] !font-medium"
            html={snippet}
          />
        </div>
      </div>
      <SignUpModal isOpen={showSignUp} onClose={handleCloseSignUp} />
    </div>
  )
}
